import React, { useMemo } from 'react';
import { MessageBar } from 'office-ui-fabric-react/lib/MessageBar';
import { Text } from 'office-ui-fabric-react/lib/Text';
import PropTypes from 'prop-types';
import styles from './AdjustmentStatusMessage.style';
import {
  AdjustmentStatus,
  hasCalculatedUIStatus,
  getDisplayableLineItemChangesForReversals,
} from '../../utils/adjustmentUtils';

const renderMessageContents = (adjustment, isReviewer) => {
  if (hasCalculatedUIStatus(adjustment, AdjustmentStatus.Draft)) {
    return (
      <>
        <Text className={styles.thisIsADraftTitle}>This is a draft</Text>
        <br />
        <Text className={styles.thisIsADraftMessage}>
          The adjustment has been saved but not yet submitted for approval. Review the adjustment
          and make updates as necessary.
        </Text>
      </>
    );
  }

  if (
    hasCalculatedUIStatus(adjustment, AdjustmentStatus.ApprovalRequested) ||
    hasCalculatedUIStatus(adjustment, AdjustmentStatus.ReviewRequested)
  ) {
    if (isReviewer) {
      return (
        <>
          <Text className={styles.thisIsADraftTitle}>Action Required</Text>
          <br />
          <Text className={styles.thisIsADraftMessage}>
            {`${adjustment.creator.name} submitted adjustment for review. Please review and submit approval or rejection.`}
          </Text>
        </>
      );
    }

    return (
      <>
        <Text className={styles.thisIsADraftTitle}>
          {hasCalculatedUIStatus(adjustment, AdjustmentStatus.ApprovalRequested) &&
            'Approval Requested'}
          {hasCalculatedUIStatus(adjustment, AdjustmentStatus.ReviewRequested) &&
            'Review Requested'}
        </Text>
        <br />
        <Text className={styles.thisIsADraftMessage}>
          The adjustment has been submitted for{' '}
          {hasCalculatedUIStatus(adjustment, AdjustmentStatus.ApprovalRequested) && 'approval'}
          {hasCalculatedUIStatus(adjustment, AdjustmentStatus.ReviewRequested) && 'review'}.
        </Text>
      </>
    );
  }

  if (hasCalculatedUIStatus(adjustment, AdjustmentStatus.ReversalDraft)) {
    return (
      <>
        <Text className={styles.thisIsADraftTitle}>This is a draft of a reversal request</Text>
        <br />
        <Text className={styles.thisIsADraftMessage}>
          The reversal request for this adjustment has been saved as a draft. Review the adjustment
          and make updates as necessary.
        </Text>
      </>
    );
  }

  if (hasCalculatedUIStatus(adjustment, AdjustmentStatus.ReversalApprovalRequested)) {
    const lineItemChanges = getDisplayableLineItemChangesForReversals(adjustment);

    let subtitle = `The adjustment has been submitted and is pending reversal review for line items:`;

    if (lineItemChanges.length === 1) {
      subtitle = `The adjustment has been submitted and is pending reversal review for line item ${lineItemChanges[0].title}.`;
      lineItemChanges[0].title = null;
    }

    return (
      <>
        <Text className={styles.thisIsADraftTitle}>Pending Reversal Review</Text>
        <br />
        <Text className={styles.thisIsADraftMessage}>{subtitle}</Text>
        {lineItemChanges.map(lineItemChange => (
          <div key={lineItemChange._id} style={{ marginTop: '1em' }}>
            {lineItemChange.title && <Text>{lineItemChange.title}</Text>}
            <ul>
              {lineItemChange.changes.map(change => (
                <li key={change} style={{ fontWeight: 400, marginLeft: '1em' }}>
                  {change}
                </li>
              ))}
            </ul>
          </div>
        ))}
      </>
    );
  }

  return null;
};

const AdjustmentStatusMessage = ({ adjustment, isReviewer }) => {
  const message = useMemo(
    () => renderMessageContents(adjustment, isReviewer),
    [adjustment, isReviewer],
  );

  if (!message) {
    return null;
  }

  return (
    <>
      <MessageBar
        data-testid={`adjustment-${adjustment._id}-message-bar`}
        isMultiline
        className={styles.messageBar}
      >
        {message}
      </MessageBar>
    </>
  );
};

AdjustmentStatusMessage.propTypes = {
  adjustment: PropTypes.object.isRequired,
  isReviewer: PropTypes.bool.isRequired,
};

export default AdjustmentStatusMessage;
